import { Mutex } from 'async-mutex';
import { jwtDecode } from 'jwt-decode';

import { type RegisterData } from '$/components/core/Authentication/components/RegisterForm';
import { type ConsentData } from '$/components/core/Authentication/components/SapCxConsentForm';
import { type LinkingData } from '$/components/core/Authentication/components/SapCxLinkingForm';
import { type MigrationData } from '$/components/core/Authentication/components/SapCxMigrationForm';
import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import { detectLanguageAndCountry } from '$/components/core/Localization/18n';
import { router } from '$/routes';
import { queryClient } from '$/services/client';
import { backendEndpoints } from '$/services/endpoints';
import { fetcher } from '$/services/fetcher';
import { GeneralBackendResponseObjectSchema } from '$/services/mapper/authentication';
import {
  RegistrationResponseSchema,
  type User,
} from '$/services/usecases/authentication/mapper/genericCloudResponse';
import { type UserRole } from '$/services/usecases/authentication/mapper/jwt';
import { SignInSchema } from '$/services/usecases/authentication/mapper/signin';
import { ResendVerificationOtpResponseSchema } from '$/services/usecases/authentication/mapper/signup';
import { useFavoriteStore } from '$/stores/useFavoriteStore';

declare global {
  interface Window {
    flutter_inappwebview: {
      callHandler: (
        handlerName: string,
        ...args: unknown[]
      ) => Promise<{
        user: User | undefined;
        userRole: UserRole;
        accessToken: string | undefined;
        refreshToken: string | undefined;
      }>;
    };
    __SPECTRUM_AUTH__?: {
      accessToken: string;
      refreshToken: string;
      flutterCallbackChannel?: string;
    };
  }
}

const mutex = new Mutex();

export type LoginData = {
  email: string;
  password: string;
};

export const registerUser = async (data: RegisterData) => {
  const { country } = await detectLanguageAndCountry();
  return await fetcher(backendEndpoints.REGISTER_URL, {
    body: {
      firstname: data.firstName,
      lastname: data.lastName,
      ...data,
      country,
    },
    method: 'POST',
    parser: (data) => RegistrationResponseSchema.parse(data),
  });
};

export const verifyUser = async (otpToken: string, otpCode: string) => {
  return await fetcher(backendEndpoints.VERIFY_USER_URL, {
    body: {
      otpToken,
      otpCode,
    },
    method: 'POST',
    parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
  });
};

export const resendVerificationOtpMail = async (email: string) => {
  return await fetcher(backendEndpoints.RESEND_VERIFICATION_URL, {
    body: {
      email,
    },
    method: 'POST',
    parser: (data) => ResendVerificationOtpResponseSchema.parse(data),
  });
};

export const signInUser = async ({ email, password }: LoginData) => {
  const { country } = await detectLanguageAndCountry();

  const res = await fetcher(backendEndpoints.SIGN_IN_URL, {
    body: {
      email,
      password,
      country,
    },
    method: 'POST',
    parser: (data) => SignInSchema.parse(data),
    includeCredentials: true,
  });

  if (res.isSuccessful) {
    useAuthenticationStore.setState({ refreshFailed: false });
    await useFavoriteStore.getState().getFavorites(true);
  }

  return res;
};

export const signInViaOidcToken = async ({
  code,
  redirectUrl,
}: {
  code: string;
  redirectUrl?: string;
}) => {
  const res = await fetcher(backendEndpoints.SAPCX_LOGIN_URL, {
    body: {
      code,
      redirectUrl,
    },
    method: 'POST',
    parser: (data) => SignInSchema.parse(data),
    includeCredentials: true,
  });

  if (res.isSuccessful) {
    useAuthenticationStore.setState({ refreshFailed: false });
    await useFavoriteStore.getState().getFavorites(true);
  }

  return res;
};

export const migrateUser = async (data: MigrationData) => {
  const { country } = await detectLanguageAndCountry();
  const res = await fetcher(backendEndpoints.SAPCX_MIGRATE_URL, {
    body: {
      ...data,
      country,
    },
    method: 'POST',
    parser: (data) => SignInSchema.parse(data),
  });

  if (res.isSuccessful) {
    useAuthenticationStore.setState({ refreshFailed: false });
    await useFavoriteStore.getState().getFavorites(true);
  }

  return res;
};

export const consentTerms = async (data: ConsentData) => {
  const { country } = await detectLanguageAndCountry();
  const res = await fetcher(backendEndpoints.SAPCX_CONSENT_URL, {
    body: {
      ...data,
      country,
    },
    method: 'POST',
    parser: (data) => SignInSchema.parse(data),
  });

  if (res.isSuccessful) {
    useAuthenticationStore.setState({ refreshFailed: false });
    await useFavoriteStore.getState().getFavorites(true);
  }

  return res;
};

export const linkSapCxAccount = async (data: LinkingData) => {
  return await fetcher(backendEndpoints.SAPCX_LINKING_URL, {
    body: data,
    method: 'POST',
    parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
  });
};

export const forgotPassword = async (email: string) => {
  return await fetcher(backendEndpoints.RESET_PASSWORD_INITIATE_URL, {
    body: {
      email,
    },
    method: 'POST',
    parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
  });
};

export const resetPasswort = async (
  newPassword: string,
  otp: string,
  email: string,
) => {
  return await fetcher(backendEndpoints.RESET_PASSWORD_FULLFILL_URL, {
    body: {
      otp,
      password: newPassword,
      email,
    },
    method: 'POST',
    parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
  });
};

export const validateToken = async (skipValidCheck: boolean = false) => {
  const { accessToken, refreshToken, refreshFailed, flutterCallbackChannel } =
    useAuthenticationStore.getState();

  if (flutterCallbackChannel) {
    try {
      const newToken = await window.flutter_inappwebview.callHandler(
        flutterCallbackChannel,
      );

      if (
        !newToken?.user ||
        !newToken?.accessToken ||
        !newToken?.refreshToken
      ) {
        throw new Error('Flutter auth data incomplete');
      }

      // TODO ? user und role in spa laden? Anstatt injection via flutter_inappwebview.callHandler

      useAuthenticationStore.setState({
        accessToken: newToken.accessToken,
        user: newToken.user,
        userRole: newToken.userRole,
        refreshToken: newToken.refreshToken,
      });
    } catch (e) {
      console.error('Error refreshing webview token', e);
      useAuthenticationStore.setState({
        userRole: 'free',
        user: null,
        accessToken: '',
        refreshToken: '',
        refreshFailed: true,
      });
    }
    return;
  }

  const expiry = accessToken != '' ? (jwtDecode(accessToken)?.exp ?? 0) : 0;

  if (refreshFailed) return;

  if (skipValidCheck || expiry < Date.now() / 1000) {
    if (mutex.isLocked()) {
      await mutex.waitForUnlock();
      return;
    }

    await mutex.runExclusive(async () => {
      try {
        const res = await fetcher(backendEndpoints.SIGN_IN_REFRESH_URL, {
          method: 'POST',
          parser: (data) => SignInSchema.parse(data),
          includeCredentials: true,
          body: refreshToken ? { refreshToken } : {},
        });
        if (res.isSuccessful) {
          useAuthenticationStore.setState({
            accessToken: res.response.accessToken,
            user: res.response.user,
            userRole: res.response.user?.role,
            refreshToken: '',
          });
        } else {
          useAuthenticationStore.setState({
            userRole: 'free',
            user: null,
            accessToken: '',
            refreshToken: '',
            refreshFailed: true,
          });
        }
      } catch (e) {
        useAuthenticationStore.setState({
          userRole: 'free',
          user: null,
          accessToken: '',
          refreshToken: '',
          refreshFailed: true,
        });
        console.error(
          'Error refreshing token',
          e instanceof Error ? e : new Error(String(e)),
        );
      }
    });
  }
};

export const signOut = async () => {
  const response = await fetcher(backendEndpoints.SIGN_OUT_URL, {
    method: 'POST',
    parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
    includeCredentials: true,
    body: {},
  });

  if (response.isSuccessful) {
    useAuthenticationStore.setState({
      user: null,
      accessToken: '',
      userRole: 'free',
    });
    await useFavoriteStore.getState().getFavorites(true);
    queryClient.clear();

    await router.navigate({ to: '/' });
  }

  return response;
};

export const setupWebviewAuthIntegration = () => {
  if (typeof window === 'undefined') return;

  window.addEventListener('spectrum-auth-ready', async () => {
    try {
      const authData = window.__SPECTRUM_AUTH__;

      if (authData?.flutterCallbackChannel) {
        useAuthenticationStore.setState({
          flutterCallbackChannel: authData.flutterCallbackChannel,
          refreshFailed: false,
        });
      } else {
        if (!authData?.accessToken || !authData?.refreshToken) {
          console.error('Webview auth data incomplete');
          useAuthenticationStore.setState({
            userRole: 'free',
            user: null,
            accessToken: '',
            refreshToken: '',
            refreshFailed: true,
          });
          return;
        }

        const { accessToken, refreshToken } = authData;

        useAuthenticationStore.setState({
          accessToken,
          refreshToken,
          refreshFailed: false,
        });
      }

      delete window.__SPECTRUM_AUTH__;

      await validateToken(true);
    } catch (error) {
      console.error('Error during webview auth integration:', error);
      useAuthenticationStore.setState({
        userRole: 'free',
        user: null,
        accessToken: '',
        refreshToken: '',
        refreshFailed: true,
      });
    }
  });
};
